import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, FormControl, InputLabel, Select, MenuItem,
  Alert, IconButton, InputAdornment, Box, Typography, Divider,
  CircularProgress, Collapse, Tooltip, Chip
} from '@mui/material';
import {
  Visibility, VisibilityOff, AddCircle, RemoveCircle
} from '@mui/icons-material';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';

const AddClientForm = ({ open, onClose, onSuccess, banques = [] }) => {
  const theme = useTheme();
  
  const initialFormData = {
    codeClient: '', // Nom du champ tel qu'attendu par le serveur
    typePersonne: 'morale',
    raisonSociale: '',
    nom: '',
    prenom: '',
    codeTva: '',
    cin: '',
    email: '',
    emailSecondaire: '',
    telephone: '',
    adresse: '',
    activite: '',
    idRne: '',
    tejLogin: '',
    tejPassword: '',
    comptesBancaires: [{ rib: '', banqueLogin: '', banquePassword: '', banque: '' }]
  };

  const [formData, setFormData] = useState(initialFormData);
  const [showPassword, setShowPassword] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');

  const isPhysique = formData.typePersonne === 'physique';

  useEffect(() => {
    if (open) {
      setFormData(initialFormData);
      setErrors({});
      setServerError('');
      setShowPassword({});
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Convertir codeClient en majuscules
    if (name === 'codeClient') {
      processedValue = value.toUpperCase();
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    
    // Nettoyer l'erreur pour ce champ s'il y en a une
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCompteChange = (index, field, value) => {
    const comptes = [...formData.comptesBancaires];
    comptes[index][field] = value;
    setFormData(prev => ({ ...prev, comptesBancaires: comptes }));
    
    // Nettoyer l'erreur si elle existe
    const errorKey = `${field}_${index}`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const addCompte = () => {
    setFormData(prev => ({
      ...prev,
      comptesBancaires: [...prev.comptesBancaires, { rib: '', banqueLogin: '', banquePassword: '', banque: '' }]
    }));
    setShowPassword(prev => ({ ...prev, [formData.comptesBancaires.length]: false }));
  };

  const removeCompte = (index) => {
    const comptes = formData.comptesBancaires.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, comptesBancaires: comptes }));
    setShowPassword(prev => {
      const newShowPassword = { ...prev };
      delete newShowPassword[index];
      return newShowPassword;
    });
    
    // Nettoyer les erreurs pour ce compte supprimé
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`rib_${index}`];
      delete newErrors[`banque_${index}`];
      delete newErrors[`banqueLogin_${index}`];
      delete newErrors[`banquePassword_${index}`];
      return newErrors;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Validation du code client
    if (!formData.codeClient.trim()) {
      newErrors.codeClient = 'Code client requis';
    } else if (formData.codeClient.length !== 7) {
      newErrors.codeClient = 'Le code client doit contenir exactement 7 caractères';
    }

    // Validation email principal
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    // Validation email secondaire
    if (formData.emailSecondaire && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailSecondaire)) {
      newErrors.emailSecondaire = 'Email secondaire invalide';
    }

    // Validation selon le type de personne
    if (formData.typePersonne === 'morale') {
      if (!formData.raisonSociale.trim()) {
        newErrors.raisonSociale = 'Raison sociale requise';
      }
    } else {
      if (!formData.nom.trim()) {
        newErrors.nom = 'Nom requis';
      }
      if (!formData.prenom.trim()) {
        newErrors.prenom = 'Prénom requis';
      }
      if (!formData.cin.trim()) {
        newErrors.cin = 'CIN requis pour personne physique';
      }
    }

    // Validation TEJ
    if (!formData.tejLogin.trim()) {
      newErrors.tejLogin = 'Login TEJ requis';
    }

    // Validation des comptes bancaires
    formData.comptesBancaires.forEach((compte, index) => {
      // Validation du RIB (seulement si rempli)
      if (compte.rib && compte.rib.trim() !== '') {
        const cleaned = compte.rib.replace(/\D/g, '');
        if (cleaned.length !== 20) {
          newErrors[`rib_${index}`] = 'Le RIB doit contenir exactement 20 chiffres';
        }
      }
      
      // La banque n'est pas requise
      // Les identifiants banque ne sont requis que si le RIB est rempli
      if (compte.rib && compte.rib.trim() !== '') {
        if (!compte.banqueLogin || compte.banqueLogin.trim() === '') {
          newErrors[`banqueLogin_${index}`] = 'Login banque requis quand RIB est fourni';
        }
        if (!compte.banquePassword || compte.banquePassword.trim() === '') {
          newErrors[`banquePassword_${index}`] = 'Mot de passe banque requis quand RIB est fourni';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setServerError('');
    setLoading(true);

    try {
      // Préparer les données pour l'envoi
      const dataToSend = {
        ...formData,
        emailSecondaire: formData.emailSecondaire || undefined,
        idRne: formData.idRne || undefined,
        comptesBancaires: formData.comptesBancaires
          .filter(compte => compte.rib && compte.rib.trim() !== '') // Ne garder que les comptes avec RIB
          .map(compte => ({
            rib: compte.rib ? compte.rib.replace(/\D/g, '') : '',
            banqueLogin: compte.banqueLogin,
            banquePassword: compte.banquePassword,
            banque: compte.banque && compte.banque.trim() !== '' ? compte.banque : undefined
          }))
      };

      const response = await axios.post('/api/comptable/clients', dataToSend);
      onSuccess(response.data._id);
      onClose();
    } catch (err) {
      setServerError(err.response?.data?.error || "Erreur lors de la création du client.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="add-client-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[8],
          bgcolor: 'background.paper',
          m: { xs: 1, sm: 2 }
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle
          id="add-client-dialog-title"
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Typography variant="h6" component="h2" fontWeight="bold">
            Ajouter un nouveau client
          </Typography>
          <Chip 
            label="Nouveau" 
            color="secondary" 
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 3, sm: 4 }, bgcolor: 'background.default' }}>
          <Collapse in={!!serverError}>
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 1 }}
              onClose={() => setServerError('')}
            >
              {serverError}
            </Alert>
          </Collapse>

          {/* Informations Générales */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" color="text.primary" fontWeight="medium" gutterBottom>
              Informations Générales
            </Typography>
            <Divider sx={{ mb: 3, borderColor: 'grey.300' }} />
            <Grid container spacing={3}>
              {/* Ligne 1: Code Client et Type de personne */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Code Client"
                  name="codeClient"
                  fullWidth
                  required
                  value={formData.codeClient}
                  onChange={handleChange}
                  error={!!errors.codeClient}
                  helperText={errors.codeClient || '7 caractères (lettres/chiffres)'}
                  variant="outlined"
                  inputProps={{ maxLength: 7 }}
                  aria-describedby="code-client-helper"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel id="type-personne-label">Type de personne</InputLabel>
                  <Select
                    labelId="type-personne-label"
                    name="typePersonne"
                    value={formData.typePersonne}
                    onChange={handleChange}
                    label="Type de personne"
                    aria-describedby="type-personne-helper"
                  >
                    <MenuItem value="morale">Personne morale</MenuItem>
                    <MenuItem value="physique">Personne physique</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Champs conditionnels selon type de personne */}
              {formData.typePersonne === 'morale' ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Raison sociale"
                      name="raisonSociale"
                      fullWidth
                      value={formData.raisonSociale}
                      onChange={handleChange}
                      required
                      error={!!errors.raisonSociale}
                      helperText={errors.raisonSociale}
                      variant="outlined"
                      aria-describedby="raison-sociale-helper"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="ID RNE"
                      name="idRne"
                      fullWidth
                      value={formData.idRne}
                      onChange={handleChange}
                      variant="outlined"
                      aria-describedby="id-rne-helper"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Tooltip title="Format: XX123456789" placement="top">
                      <TextField
                        label="Code TVA"
                        name="codeTva"
                        fullWidth
                        value={formData.codeTva}
                        onChange={handleChange}
                        variant="outlined"
                        helperText={errors.codeTva}
                        aria-describedby="code-tva-helper"
                      />
                    </Tooltip>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Nom"
                      name="nom"
                      fullWidth
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      error={!!errors.nom}
                      helperText={errors.nom}
                      variant="outlined"
                      aria-describedby="nom-helper"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Prénom"
                      name="prenom"
                      fullWidth
                      value={formData.prenom}
                      onChange={handleChange}
                      required
                      error={!!errors.prenom}
                      helperText={errors.prenom}
                      variant="outlined"
                      aria-describedby="prenom-helper"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="CIN"
                      name="cin"
                      fullWidth
                      value={formData.cin}
                      onChange={handleChange}
                      required
                      error={!!errors.cin}
                      helperText={errors.cin || "Numéro de carte d'identité nationale"}
                      variant="outlined"
                      aria-describedby="cin-helper"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="ID RNE"
                      name="idRne"
                      fullWidth
                      value={formData.idRne}
                      onChange={handleChange}
                      variant="outlined"
                      aria-describedby="id-rne-helper"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Tooltip title="Format: XX123456789" placement="top">
                      <TextField
                        label="Code TVA"
                        name="codeTva"
                        fullWidth
                        value={formData.codeTva}
                        onChange={handleChange}
                        variant="outlined"
                        helperText={errors.codeTva}
                        aria-describedby="code-tva-helper"
                      />
                    </Tooltip>
                  </Grid>
                </>
              )}

              {/* Ligne: Emails */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email Principal"
                  name="email"
                  fullWidth
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  variant="outlined"
                  aria-describedby="email-helper"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email Secondaire"
                  name="emailSecondaire"
                  fullWidth
                  type="email"
                  value={formData.emailSecondaire}
                  onChange={handleChange}
                  error={!!errors.emailSecondaire}
                  helperText={errors.emailSecondaire}
                  variant="outlined"
                  aria-describedby="email-secondaire-helper"
                />
              </Grid>

              {/* Ligne: Téléphone et Activité */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Téléphone"
                  name="telephone"
                  fullWidth
                  value={formData.telephone}
                  onChange={handleChange}
                  variant="outlined"
                  aria-describedby="telephone-helper"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Activité"
                  name="activite"
                  fullWidth
                  value={formData.activite}
                  onChange={handleChange}
                  variant="outlined"
                  aria-describedby="activite-helper"
                />
              </Grid>

              {/* Adresse (pleine largeur) */}
              <Grid item xs={12}>
                <TextField
                  label="Adresse"
                  name="adresse"
                  fullWidth
                  value={formData.adresse}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  variant="outlined"
                  aria-describedby="adresse-helper"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Identifiants TEJ */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" color="text.primary" fontWeight="medium" gutterBottom>
              Identifiants TEJ
            </Typography>
            <Divider sx={{ mb: 3, borderColor: 'grey.300' }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Login TEJ"
                  name="tejLogin"
                  fullWidth
                  required
                  value={formData.tejLogin}
                  onChange={handleChange}
                  error={!!errors.tejLogin}
                  helperText={errors.tejLogin}
                  variant="outlined"
                  aria-describedby="tej-login-helper"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Mot de passe TEJ"
                  name="tejPassword"
                  fullWidth
                  type={showPassword.tej ? 'text' : 'password'}
                  value={formData.tejPassword}
                  onChange={handleChange}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(prev => ({ ...prev, tej: !prev.tej }))}
                          edge="end"
                          aria-label={showPassword.tej ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        >
                          {showPassword.tej ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  aria-describedby="tej-password-helper"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Comptes bancaires */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" color="text.primary" fontWeight="medium">
                Comptes bancaires
              </Typography>
              <Tooltip title="Ajouter un compte bancaire">
                <IconButton color="primary" onClick={addCompte} sx={{ ml: 1 }} aria-label="Ajouter un compte bancaire">
                  <AddCircle />
                </IconButton>
              </Tooltip>
            </Box>
            <Divider sx={{ mb: 3, borderColor: 'grey.300' }} />

            {formData.comptesBancaires.map((compte, index) => (
              <Box
                key={index}
                sx={{
                  mb: 3,
                  p: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                  boxShadow: theme.shadows[1]
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="RIB"
                      fullWidth
                      value={compte.rib}
                      onChange={(e) => handleCompteChange(index, 'rib', e.target.value)}
                      variant="outlined"
                      error={!!errors[`rib_${index}`]}
                      helperText={errors[`rib_${index}`] || 'Si rempli, doit contenir 20 chiffres'}
                      placeholder="12345678901234567890"
                      aria-describedby={`rib-helper-${index}`}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id={`banque-label-${index}`}>Banque (optionnel)</InputLabel>
                      <Select
                        labelId={`banque-label-${index}`}
                        value={compte.banque}
                        onChange={(e) => handleCompteChange(index, 'banque', e.target.value)}
                        label="Banque (optionnel)"
                        aria-describedby={`banque-helper-${index}`}
                      >
                        <MenuItem value="">
                          <em>Sélectionner une banque (optionnel)</em>
                        </MenuItem>
                        {banques.map(b => (
                          <MenuItem key={b._id} value={b._id}>{b.nom}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Login Banque"
                      fullWidth
                      value={compte.banqueLogin}
                      onChange={(e) => handleCompteChange(index, 'banqueLogin', e.target.value)}
                      variant="outlined"
                      error={!!errors[`banqueLogin_${index}`]}
                      helperText={errors[`banqueLogin_${index}`] || 'Requis si RIB est fourni'}
                      aria-describedby={`banque-login-helper-${index}`}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Mot de passe Banque"
                      fullWidth
                      type={showPassword[index] ? 'text' : 'password'}
                      value={compte.banquePassword}
                      onChange={(e) => handleCompteChange(index, 'banquePassword', e.target.value)}
                      variant="outlined"
                      error={!!errors[`banquePassword_${index}`]}
                      helperText={errors[`banquePassword_${index}`] || 'Requis si RIB est fourni'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(prev => ({ ...prev, [index]: !prev[index] }))}
                              edge="end"
                              aria-label={showPassword[index] ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                            >
                              {showPassword[index] ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      aria-describedby={`banque-password-helper-${index}`}
                    />
                  </Grid>
                  
                  {formData.comptesBancaires.length > 1 && (
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<RemoveCircle />}
                        onClick={() => removeCompte(index)}
                        sx={{ textTransform: 'none', mt: 1 }}
                        aria-label={`Supprimer le compte bancaire ${index + 1}`}
                      >
                        Supprimer ce compte
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 2, justifyContent: 'flex-end' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="secondary"
            sx={{ minWidth: 120, borderRadius: 1 }}
            aria-label="Annuler la création du client"
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ minWidth: 120, borderRadius: 1 }}
            startIcon={loading && <CircularProgress size={20} />}
            aria-label={loading ? 'Création en cours' : 'Créer le client'}
          >
            {loading ? 'Création...' : 'Créer le client'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddClientForm;